import { isArray } from "ArrayUtils";
import { type DependencyResult, getAvailableResult, getNullAvailableResult } from "DependencyResult";
import type {
  DependencyAccessMixedValue,
  DependencyAccessValue,
  DependencyArgument,
  DependencyVisitorContext,
} from "DependencyVisitorContext";
import { DependencyError } from "Errors";
import { ensureIsFiniteNumber, isFiniteNumber } from "NumericService";
import { State } from "StateConstants";
import Big from "big.js";

function all<Mode>(
  collection: DependencyArgument,
  predicate: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, boolean | null> {
  return context.strategy.waitAvailableOne(collection, data, context, (c) => allCore(c, predicate, context));
}

function allCore<Mode>(
  collection: unknown,
  predicate: DependencyArgument,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, boolean | null> {
  if (collection == null) {
    return getNullAvailableResult<boolean>();
  } else if (!isArray(collection)) {
    throw new DependencyError("All() can only be used on a collection.");
  } else if (!collection.length) {
    return getAvailableResult(true);
  } else {
    return context.strategy.map(
      collection,
      (item) => predicate(item, context),
      (predicateResults) => {
        let nonAvailableResult;
        for (let i = 0; i < predicateResults.length; i++) {
          const predicateResult = predicateResults[i];
          if (predicateResult.state === State.Available) {
            if (!predicateResult.value) {
              return getAvailableResult(false);
            }
          } else if (!nonAvailableResult || predicateResult.state === State.NotAvailable) {
            nonAvailableResult = predicateResult;
          }
        }
        return nonAvailableResult || getAvailableResult(true);
      },
    );
  }
}

function any<Mode>(
  collection: DependencyArgument,
  predicate: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, boolean | null> {
  return context.strategy.waitAvailableOne(collection, data, context, (c) => anyCore(c, predicate, context));
}

function anyCore<Mode>(
  collection: unknown,
  predicate: DependencyArgument,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, boolean | null> {
  if (collection == null) {
    return getNullAvailableResult<boolean>();
  } else if (!isArray(collection)) {
    throw new DependencyError("Any() can only be used on a collection.");
  } else if (!collection.length) {
    return getAvailableResult(false);
  } else if (!predicate) {
    return getAvailableResult(true);
  } else {
    return context.strategy.map(
      collection,
      (item) => predicate(item, context),
      (predicateResults) => {
        let nonAvailableResult;
        for (let i = 0; i < predicateResults.length; i++) {
          const predicateResult = predicateResults[i];
          if (predicateResult.state === State.Available) {
            if (predicateResult.value) {
              return getAvailableResult(true);
            }
          } else if (!nonAvailableResult || predicateResult.state === State.NotLoaded) {
            nonAvailableResult = predicateResult;
          }
        }
        return nonAvailableResult || getAvailableResult(false);
      },
    );
  }
}

function count<Mode>(
  collection: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, number | null> {
  return context.strategy.waitAvailableOne(collection, data, context, countCore);
}

function countCore(collection: unknown): DependencyResult<number | null> {
  if (collection == null) {
    return getNullAvailableResult<number>();
  } else if (!isArray(collection)) {
    throw new DependencyError("Count() can only be used on a collection.");
  } else {
    return getAvailableResult(collection.length);
  }
}

function first<Mode>(
  collection: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, unknown> {
  return context.strategy.waitAvailableOne(collection, data, context, firstCore);
}

function firstCore(collection: unknown): DependencyResult {
  if (collection == null) {
    return getNullAvailableResult();
  } else if (!isArray(collection)) {
    throw new DependencyError("First() can only be used on a collection.");
  } else {
    return getAvailableResult(collection.length ? collection[0] : null);
  }
}

function orderBy<Mode>(
  collection: DependencyArgument,
  clauses: Clause<Mode>[],
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, unknown[] | null> {
  return context.strategy.waitAvailableOne(collection, data, context, (c) => orderByCore(c, clauses, context));
}

function orderByCore<Mode>(
  collection: unknown,
  clauses: Clause<Mode>[],
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, unknown[] | null> {
  if (collection == null) {
    return getNullAvailableResult<unknown[]>();
  } else if (!isArray(collection)) {
    throw new DependencyError("OrderBy() can only be used on a collection.");
  } else if (!collection.length) {
    return getAvailableResult([]);
  } else {
    return context.strategy.map(
      collection,
      (item) =>
        context.strategy.map(
          clauses,
          (c) => c.reducer(item, context),
          (value) => ({ item, state: State.Available, value }),
        ),
      (results) => {
        const resultsWithItem = results as DependencyResultWithItem<DependencyResult[]>[];
        for (let i = 0; i < resultsWithItem.length; i++) {
          const { value } = resultsWithItem[i];
          for (let j = 0; j < value.length; j++) {
            if (value[j].state !== State.Available) {
              return getAvailableResult(collection);
            }
          }
        }

        return getSortedData(clauses, resultsWithItem);
      },
    );
  }
}

function getSortedData<Mode>(
  clauses: Clause<Mode>[],
  results: DependencyResultWithItem<DependencyResult[]>[],
): DependencyResult<unknown[]> {
  const sortedData = results
    .sort((left, right) => {
      for (let i = 0; i < clauses.length; i++) {
        const { isDescending } = clauses[i];
        const leftValue = left.value[i].value;
        const rightValue = right.value[i].value;
        if (leftValue == null) {
          return rightValue == null ? 0 : isDescending ? 1 : -1;
        } else if (rightValue == null) {
          return isDescending ? -1 : 1;
        } else if (leftValue < rightValue) {
          return isDescending ? 1 : -1;
        } else if (leftValue > rightValue) {
          return isDescending ? -1 : 1;
        }
      }

      return 0;
    })
    .map(({ item }) => item);

  return getAvailableResult(sortedData);
}

function skip<Mode>(
  collection: DependencyArgument,
  count: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessValue<Mode, unknown[] | null> {
  return context.strategy.waitAvailable([collection, count], data, context, skipCore);
}

function skipCore([collection, count]: unknown[]): DependencyResult<unknown[] | null> {
  if (collection == null) {
    return getNullAvailableResult<unknown[]>();
  } else if (!isArray(collection)) {
    throw new DependencyError("Skip() can only be used on a collection.");
  }
  return getAvailableResult(collection.slice(ensureIsFiniteNumber(count)));
}

function sum<Mode>(
  collection: DependencyArgument,
  expr: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, number> {
  return context.strategy.waitAvailableOne(collection, data, context, (c) => sumCore(c, expr, context));
}

function sumCore<Mode>(
  collection: unknown,
  expr: DependencyArgument,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, number> {
  if (collection == null) {
    return getAvailableResult(0);
  } else if (!isArray(collection)) {
    throw new DependencyError(
      "Sum() can only be used on a collection of numbers or an entity collection specifying a numeric property name.",
    );
  } else if (collection.length === 0) {
    return getAvailableResult(0);
  } else {
    return context.strategy.map(
      collection,
      (item) => (expr == null ? getAvailableResult(item) : expr(item, context)),
      (predicateResults) => {
        let result = new Big(0);
        for (let i = 0; i < predicateResults.length; i++) {
          const value = predicateResults[i].value;

          if (value != null) {
            if (!isFiniteNumber(value)) {
              throw new DependencyError("Can only use Sum on numeric types.");
            } else {
              result = result.plus(value);
            }
          }
        }
        return getAvailableResult(result.toNumber());
      },
    );
  }
}

function take<Mode>(
  collection: DependencyArgument,
  count: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessValue<Mode, unknown[] | null> {
  return context.strategy.waitAvailable([collection, count], data, context, takeCore);
}

function takeCore([collection, count]: unknown[]): DependencyResult<unknown[] | null> {
  if (collection == null) {
    return getNullAvailableResult<unknown[]>();
  } else if (!isArray(collection)) {
    throw new DependencyError("Take() can only be used on a collection.");
  }
  return getAvailableResult(collection.slice(0, ensureIsFiniteNumber(count)));
}

function where<Mode>(
  collection: DependencyArgument,
  predicate: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, unknown[] | null> {
  return context.strategy.waitAvailableOne(collection, data, context, (c) => whereCore(c, predicate, context));
}

function whereCore<Mode>(
  collection: unknown,
  predicate: DependencyArgument,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, unknown[] | null> {
  if (collection == null) {
    return getNullAvailableResult<unknown[]>();
  } else if (!isArray(collection)) {
    throw new DependencyError("Where() can only be used on a collection.");
  } else if (collection.length === 0) {
    return getAvailableResult([]);
  } else {
    return context.strategy.map(
      collection,
      (item) => predicate(item, context),
      (predicateResults) => {
        const result = [];
        for (let i = 0; i < predicateResults.length; i++) {
          const predicateResult = predicateResults[i];
          if (predicateResult.state !== State.Available) {
            return predicateResult;
          } else {
            const { value } = predicateResult;
            if (value != null && typeof value !== "boolean") {
              throw new DependencyError("Predicate for Where() must return a boolean value.");
            } else if (value) {
              result.push(collection[i]);
            }
          }
        }
        return getAvailableResult(result);
      },
    );
  }
}

export interface Clause<Mode> {
  isDescending: boolean;
  reducer: (item: unknown, context: DependencyVisitorContext<Mode>) => DependencyAccessValue<Mode, DependencyResult>;
}

type DependencyResultWithItem<T> = DependencyResult<T> & { item: unknown; value: T };

export default { all, any, count, first, orderBy, skip, sum, take, where };
